<button mat-stroked-button type="button" (click)="generate()" [disabled]="busy || disabled">Refresh challenge content</button>
<p *ngIf="disabled">{{ disabledReason || 'Save your edits before refreshing challenge content.' }}</p>
<p *ngIf="busy" role="status">{{ preview ? 'Applying refresh…' : 'Generating a preview from current fundamental skills…' }}</p>
<mat-progress-bar *ngIf="busy" mode="indeterminate"></mat-progress-bar>
<p *ngIf="error" class="error" role="alert">{{ error }}</p>
<p *ngIf="message" role="status">{{ message }}</p>
<details *ngIf="!preview && !busy">
  <summary>Restore a saved preview</summary>
  <p>If the browser lost the saved job, enter its generation job ID from Hangfire. This retrieves the existing comparison without a new AI call.</p>
  <label>Generation job ID <input [(ngModel)]="restoreJobId" aria-label="Generation job ID"></label>
  <button mat-stroked-button type="button" (click)="restoreSavedPreview()" [disabled]="!restoreJobId.trim()">Restore comparison</button>
</details>
<section class="refresh-panel" *ngIf="preview as p" aria-label="Refresh challenge preview">
  <h3>Review refreshed content</h3>
  <p>Review the regenerated name, summary, description, skills, FAQ and cover image. Uncheck any field you want to preserve. Schedules, URLs, scoring settings and registrations are preserved. This preview expires at {{ p.expiresAt | date:'shortTime' }}.</p>
  <div class="fields">
    <mat-checkbox [(ngModel)]="fields.title" [disabled]="busy">Name</mat-checkbox>
    <mat-checkbox [(ngModel)]="fields.coverImage" [disabled]="busy || !p.coverImageUrl">Cover image</mat-checkbox>
    <mat-checkbox [(ngModel)]="fields.skills" [disabled]="busy">Skills</mat-checkbox>
    <mat-checkbox [(ngModel)]="fields.description" [disabled]="busy">Description and requirements</mat-checkbox>
    <mat-checkbox [(ngModel)]="fields.summary" [disabled]="busy">Summary</mat-checkbox>
    <mat-checkbox [(ngModel)]="fields.faq" [disabled]="busy">FAQ</mat-checkbox>
  </div>
  <p *ngIf="p.coverGenerationError" class="error">{{ p.coverGenerationError }}</p>
  <p *ngIf="fields.skills !== fields.description">Review carefully: refreshing skills and description together keeps requirements aligned.</p>
  <div *ngFor="let target of p.targets; let first = first">
    <h4>{{ target.title }}</h4>
    <mat-checkbox *ngIf="!first" class="weekly" [(ngModel)]="selectedWeekly[target.id]" [disabled]="busy">Also refresh this weekly challenge<span *ngIf="target.registrationClosesAt"> (registration closes {{ target.registrationClosesAt | date:'medium' }})</span></mat-checkbox>
    <details [open]="first">
      <summary>Compare current and proposed content</summary>
      <div class="comparison">
        <div>
          <h4>Current</h4>
          <img *ngIf="fields.coverImage && target.coverImageUrl" [src]="target.coverImageUrl" alt="Current challenge cover" style="max-width:100%;max-height:240px">
          <p *ngIf="fields.title"><strong>{{ target.title }}</strong></p>
          <ul *ngIf="fields.skills"><li *ngFor="let skill of target.skills">{{ skill.skillName }} / {{ skill.topicName }} — {{ skill.targetProficiency }}</li></ul>
          <div *ngIf="fields.description" class="content" [innerHTML]="markdown(target.description)"></div>
          <p *ngIf="fields.summary">{{ target.summary }}</p>
          <div *ngIf="fields.faq"><p *ngFor="let faq of target.faqItems"><strong>{{ faq.question }}</strong><br>{{ faq.answer }}</p></div>
        </div>
        <div>
          <h4>Proposed</h4>
          <img *ngIf="fields.coverImage && p.coverImageUrl" [src]="p.coverImageUrl" alt="Proposed challenge cover" style="max-width:100%;max-height:240px">
          <p *ngIf="fields.title"><strong>{{ p.content.title }}</strong></p>
          <ul *ngIf="fields.skills">
            <li *ngFor="let skill of p.content.skills">{{ skillChange(skill, target.skills) }}: {{ skill.skillName }} / {{ skill.topicName }} — {{ skill.targetProficiency }}</li>
            <li *ngFor="let skill of removedSkills(target.skills)">Removed: {{ skill.skillName }} / {{ skill.topicName }}</li>
          </ul>
          <div *ngIf="fields.description" class="content" [innerHTML]="markdown(p.content.description)"></div>
          <p *ngIf="fields.summary">{{ p.content.summary }}</p>
          <div *ngIf="fields.faq"><p *ngFor="let faq of p.content.faqItems"><strong>{{ faq.question }}</strong><br>{{ faq.answer }}</p></div>
        </div>
      </div>
    </details>
  </div>
  <p>Registered learners will be notified. Weekly challenges that change or reach registration close before saving will be skipped.</p>
  <mat-checkbox [(ngModel)]="acknowledged" [disabled]="busy">I reviewed the changes and agree to replace selected fields, including any manual edits.</mat-checkbox>
  <p *ngIf="applyDisabledReason">{{ applyDisabledReason }}</p>
  <div class="actions">
    <button *ngIf="previewExpired" mat-stroked-button type="button" (click)="renewPreview()" [disabled]="busy || disabled">Renew approval — no AI call</button>
    <button mat-flat-button type="button" (click)="apply()" [disabled]="!canApply">Apply refresh</button>
    <button mat-button type="button" (click)="cancelPreview()" [disabled]="busy">Cancel</button>
  </div>
</section>
